import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useState } from 'react';
import { Button, Form, FormProps } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import Radio, { RadioGroup } from '../Radio';
import TextInput from '../TextInput';

export type PhoneType = 'home' | 'work' | 'mobile';

export type Phone = {
  type: PhoneType;
  phone: string;
  messaging: string;
};

export type customPhoneFormProps = FormProps &
  React.PropsWithoutRef<{
    existingPhone?: Phone;
    handleClose: () => void;
    handleOnSubmit: (formData: any) => void;
  }>;

const newPhone: Phone = {
  type: 'home',
  phone: '',
  messaging: 'disabled',
};

const PhoneForm = ({
  existingPhone = newPhone,
  handleClose,
  handleOnSubmit,
  ...props
}: customPhoneFormProps) => {
  const [phone] = useState<Phone>(existingPhone);
  const [phoneType, setPhoneType] = useState<PhoneType>(phone.type);
  const [phoneMessaging, setPhoneMessaging] = useState<string>(phone.messaging);

  const isEditForm = phone['phone'] !== '';
  const phoneRegExp = /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/;

  const validationSchema = yup.object().shape({
    type: yup.string().required('Type is required'),
    phone: yup
      .string()
      .matches(phoneRegExp, 'Phone number is invalid')
      .required('A Phone number is required'),
    messaging: yup.string(),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: phone,
    resolver: yupResolver(validationSchema),
  });

  const handleSubmitForm = (formData: any) => {
    formData.messaging = phoneMessaging;
    formData.type = phoneType;
    handleOnSubmit(formData);
  };

  const handleTypeChange = (event: any) => {
    setPhoneType(event);
  };

  const handleMessagingChange = (event: any) => {
    setPhoneMessaging(event);
  };

  return (
    <Form {...props}>
      <h4 className="mb-12 text-sm-20-lg-32">
        {isEditForm ? 'Edit Phone' : 'Add New phone'}
      </h4>
      {!isEditForm && (
        <>
          <Form.Label className="text-fs-6">Type of Phone Numbers</Form.Label>
          <Controller
            name={'type'}
            control={control}
            render={({ field }) => {
              field.value = phoneType;
              return (
                <RadioGroup {...field} onChange={handleTypeChange}>
                  <Radio
                    value="home"
                    id="phone-type-home"
                    label="Home"
                    className="mt-6 text-sm-12-lg-14"
                  />
                  <Radio
                    value="work"
                    id="phone-type-work"
                    label="Work"
                    className="mt-6"
                  />
                  <Radio
                    value="mobile"
                    id="phone-type-mobile"
                    label="Mobile"
                    className="mt-6"
                  />
                </RadioGroup>
              );
            }}
          />
        </>
      )}
      {phoneType === 'mobile' && (
        <>
          <Form.Label className="text-fs-6 mt-6">
            Enabled Text Messaging
          </Form.Label>
          <Controller
            name={'messaging'}
            control={control}
            render={({ field }) => {
              field.value = phoneMessaging;
              return (
                <RadioGroup {...field} onChange={handleMessagingChange}>
                  <Radio
                    value="enabled"
                    id="messaging-true"
                    label="Enable"
                    className="mt-6 text-sm-12-lg-14"
                  />
                  <Radio
                    value="disabled"
                    id="messaging-false"
                    label="Disable"
                    className="mt-6"
                  />
                </RadioGroup>
              );
            }}
          />
        </>
      )}
      <Controller
        name={'phone'}
        control={control}
        render={({ field }) => (
          <TextInput
            type={'tel'}
            leftIcon={light('phone')}
            label="Phone Number"
            className="mt-8"
            placeholder="Phone Number"
            isInvalid={!!errors.phone}
            {...field}
          />
        )}
      />
      {errors.phone && (
        <div className="error-message">
          <span>{errors.phone.message}</span>
        </div>
      )}
      <section className="py-11 py-lg-12 d-flex justify-content-center flex-column flex-lg-row">
        <Button
          className="mt-7 mt-lg-0 me-lg-7 order-2 order-lg-1"
          variant="primary-outline"
          onMouseDown={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          className="order-1 order-lg-2"
          onClick={handleSubmit(handleSubmitForm)}
        >
          {phoneMessaging === 'enabled' ? 'Save & Verify' : 'Save'}
        </Button>
      </section>
    </Form>
  );
};

export default PhoneForm;
